import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InboxItem } from "../../store/slice/inboxSlice";
import {
  FALLBACK_FRAME_HEIGHT,
  GovernanceResponseStatus,
  respondSrc,
  responseStatusSrc,
  trimCss,
  trustedActionOrigin,
} from "../../utils/notification-actions-iframe-utils";
import styles from "./notification-actions-iframe.module.scss";

type FrameView =
  | { kind: "checking" }
  | { kind: "pending" }
  | { kind: "ready" }
  | { kind: "terminal"; title: string; message: string }
  | { kind: "unavailable"; message: string };

const STATUS_TIMEOUT_MS = 8000;
const HANDSHAKE_TIMEOUT_MS = 8000;
const GOVERNANCE_STATUS_EVENT = "art:governance-status-changed";
const TERMINAL_STATUSES = new Set([
  "responded", "approved", "rejected", "timed_out", "escalated",
  "auto_approved", "completed", "cancelled", "expired", "invalid",
  "not_found", "forbidden", "resolved",
]);

function useRevealed(ref: React.RefObject<HTMLElement | null>): boolean {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (revealed || !ref.current) return;
    if (getComputedStyle(ref.current).visibility !== "hidden") setRevealed(true);
  });

  return revealed;
}


function actionLabel(item: InboxItem): string {
  switch (String(item.data?.interaction_type || "").toLowerCase()) {
    case "approval":
      return "Approve or reject";
    case "escalate":
      return "Respond to escalation";
    case "feedback":
      return "Give feedback";
    case "inform":
      return "View details";
    default:
      return "Open request";
  }
}

function terminalView(status: GovernanceResponseStatus): FrameView {
  return {
    kind: "terminal",
    title: status.title?.trim() || "Request unavailable",
    message: status.message?.trim() || "This request is no longer awaiting your response.",
  };
}

export default function NotificationActionsIFrame({
  item,
  onResolved,
}: Readonly<{ item: InboxItem; onResolved: (item: InboxItem) => void }>) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const resolvedRef = useRef(false);
  const [height, setHeight] = useState(FALLBACK_FRAME_HEIGHT);
  const [attempt, setAttempt] = useState(0);
  const [view, setView] = useState<FrameView>({ kind: "checking" });
  const [expanded, setExpanded] = useState(false);
  const revealed = useRevealed(wrapRef);

  const src = useMemo(() => respondSrc(item), [item]);
  const statusSrc = useMemo(() => responseStatusSrc(item), [item]);
  const expectedOrigin = useMemo(() => trustedActionOrigin(src), [src]);
  const statusOrigin = useMemo(() => trustedActionOrigin(statusSrc), [statusSrc]);

  const resolvedOnce = useCallback(() => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    const requestId = typeof item.data?.request_id === "string" ? item.data.request_id : "";
    if (requestId) {
      window.dispatchEvent(new CustomEvent(GOVERNANCE_STATUS_EVENT, { detail: { requestId } }));
    }
    onResolved(item);
  }, [item, onResolved]);

  useEffect(() => () => cleanupRef.current?.(), []);

  useEffect(() => {
    resolvedRef.current = false;
    setHeight(FALLBACK_FRAME_HEIGHT);
    if (!src) return;
    if (!expectedOrigin || (statusSrc && !statusOrigin)) {
      setView({
        kind: "unavailable",
        message: "This request belongs to a different or untrusted environment.",
      });
      return;
    }
    if (!statusSrc) {
      setView({ kind: "pending" });
      return;
    }

    const controller = new AbortController();
    let active = true;
    const timeout = window.setTimeout(() => controller.abort(), STATUS_TIMEOUT_MS);
    setView({ kind: "checking" });

    fetch(statusSrc, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then(async (response) => {
        const contentType = response.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) throw new Error("invalid status response");
        const status = (await response.json()) as GovernanceResponseStatus;
        if (status.status === "pending" && status.can_respond) {
          setView({ kind: "pending" });
          return;
        }
        if (!TERMINAL_STATUSES.has(status.status)) throw new Error("invalid status response");
        setView(terminalView(status));
      })
      .catch(() => {
        if (active) {
          setView({
            kind: "unavailable",
            message: "Approval service is temporarily unavailable. Please try again.",
          });
        }
      })
      .finally(() => window.clearTimeout(timeout));

    return () => {
      active = false;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [attempt, expectedOrigin, src, statusOrigin, statusSrc]);

  useEffect(() => {

    if (view.kind !== "pending" || !revealed || !expanded) return undefined;
    const timeout = window.setTimeout(() => {
      setView({
        kind: "unavailable",
        message: "Approval service is temporarily unavailable. Please try again.",
      });
    }, HANDSHAKE_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [revealed, expanded, view.kind]);

  useEffect(() => {
    if (!src || !expectedOrigin) return undefined;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) return;
      if (event.source !== frameRef.current?.contentWindow) return;
      const msg = event.data as { source?: string; type?: string; state?: string; px?: number } | null;
      if (!msg || typeof msg !== "object" || msg.source !== "art-governance") return;
      if (msg.type === "governance:height" && typeof msg.px === "number") {
        setHeight(Math.max(32, msg.px));
      }
      if (msg.type === "governance:ready") {
        if (typeof msg.px === "number") setHeight(Math.max(32, msg.px));
        if (msg.state === "pending" || msg.state === "completed") {
          setView({ kind: "ready" });
        } else {
          setView({
            kind: "terminal",
            title: "Request resolved",
            message: "This request is no longer awaiting your response.",
          });
        }
      }
      if (msg.type === "governance:responded") {
        setView({
          kind: "terminal",
          title: "Response submitted",
          message: "Your response has been submitted.",
        });
        resolvedOnce();
      }
      if (msg.type === "governance:refresh") {
        setAttempt((current) => current + 1);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [expectedOrigin, resolvedOnce, src]);

  const handleLoad = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) return;

    let doc: Document | null = null;
    try {
      doc = frame.contentDocument;
    } catch {
      doc = null;
    }
    if (!doc?.body) return;

    const parentStyle = frame.parentElement ? getComputedStyle(frame.parentElement) : null;
    const text = parentStyle?.color || "#1e293b";
    const accent = parentStyle?.getPropertyValue("--primary-color").trim() || "#2563eb";
    const style = doc.createElement("style");
    style.textContent = trimCss(text, accent);
    doc.head.appendChild(style);

    const measure = () => {
      const tallest = Math.max(doc.body.getBoundingClientRect().height, doc.body.offsetHeight);
      setHeight(Math.max(32, Math.ceil(tallest) + 2));
    };
    measure();

    cleanupRef.current?.();
    const mutations = new MutationObserver(measure);
    mutations.observe(doc.body, { childList: true, subtree: true });
    const resizes = new ResizeObserver(measure);
    resizes.observe(doc.body);
    cleanupRef.current = () => {
      mutations.disconnect();
      resizes.disconnect();
    };
  }, []);

  if (!src) return null;

  const retry = () => setAttempt((current) => current + 1);
  const actionable = view.kind === "pending" || view.kind === "ready";

  const showFrame = revealed && expanded && actionable;

  return (
    <div ref={wrapRef} className={styles.respond_frame_wrap} onClick={(event) => event.stopPropagation()}>
      {view.kind === "checking" && <div className={styles.frame_status}>Checking request…</div>}
      {view.kind === "terminal" && (
        <div className={styles.frame_result} role="status">
          <strong>{view.title}</strong>
          <span>{view.message}</span>
        </div>
      )}
      {view.kind === "unavailable" && (
        <div className={styles.frame_result} role="alert">
          <strong>Couldn&apos;t open this request</strong>
          <span>{view.message}</span>
          <button type="button" className={styles.retry_button} onClick={retry}>Retry</button>
        </div>
      )}
      {actionable && (
        <button
          type="button"
          className={styles.toggle_button}
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          data-qa="notification-action-toggle"
        >
          {expanded ? "Hide" : actionLabel(item)}
        </button>
      )}
      {showFrame && (
        <>
          {view.kind === "pending" && <div className={styles.frame_status}>Loading response form…</div>}
          <iframe
            key={attempt}
            ref={frameRef}
            src={src}
            title={item.title || "Respond to this request"}
            className={styles.respond_frame}
            style={{ height: `${view.kind === "ready" ? height : 1}px`, visibility: view.kind === "ready" ? "visible" : "hidden" }}
            onLoad={handleLoad}
            referrerPolicy="no-referrer"
            sandbox="allow-forms allow-scripts allow-same-origin"
          />
        </>
      )}
    </div>
  );
}
